import type { Cell, Row, Table } from "@tanstack/react-table";
import type { ChangeEvent, HTMLInputTypeAttribute } from "react";
import { useMemo, useState, type HTMLAttributes, type ReactNode, useEffect } from "react";
import cx from "ui/cx";
import type { InputProps } from "ui/forms/Input";
import Input from "ui/forms/Input";
import Select from "ui/forms/Select";
import IconWarning from "ui/icons/IconWarning";
import { wrapWithTooltip } from "ui/popovers/Tooltip/withTooltip";
import styles from "./DataTable.module.scss";
import type { ColumnTypes, DataTableColumn, TableRowData, CellValue } from "./types";

export default function TableRowCell<T extends TableRowData>({
  cell,
  children,
  style,
  className,
  icon,
  isMobileView,
  tooltip,
}: {
  cell: Cell<T, unknown>;
  row: Row<T>;
  icon: ReactNode;
  isMobileView: boolean;
  tooltip?: ReactNode;
} & HTMLAttributes<HTMLDivElement>) {
  const header = String(cell.column.columnDef.header);

  return wrapWithTooltip(
    <div
      key={cell.id}
      className={cx(styles.cell, isMobileView && styles.mobileView, className)}
      style={style}
      title={typeof children === "string" && !tooltip ? children : ""}
      role="gridcell"
      data-column={header}
    >
      {isMobileView && <div className={styles.cellHeader}>{header}</div>}
      {icon}
      <div className={styles.cellContents}>{children}</div>
    </div>,
    tooltip,
  );
}

// TODO: add validation
export function EditableCellContents<T extends TableRowData>({
  cell,
  row,
  table,
  field,
  value: defaultValue,
  columnType,
  selectOptions,
  disabled,
}: {
  cell: Cell<T, unknown>;
  row: Row<T>;
  field: keyof T;
  table: Table<T>;
  columnType: ColumnTypes;
  selectOptions: DataTableColumn<T>["selectOptions"];
  disabled?: boolean;
} & Pick<InputProps, "value">) {
  const { isEmpty, parseValue, inputType } = getParserType(columnType);
  const [value, setValue] = useState(parseValue(defaultValue ?? ""));
  useEffect(() => {
    setValue(parseValue(defaultValue ?? ""));
  }, [defaultValue]);

  const [showRequired, setShowRequired] = useState(false);
  const isRequired = cell.column.columnDef.meta?.required;

  // autofocus when adding a new row
  const autofocus = useMemo(() => {
    if (row.index !== 0 || value) {
      return false;
    }
    // determine if this is the first editable field
    const editableCols = table.getAllColumns().filter((c) => !!c.columnDef.meta?.editable);
    if (editableCols?.length && editableCols[0].id === cell.column.id) {
      return row.original === table.options.meta?.emptyRow;
    }
    return false;
  }, []);

  const save = ({ e, overrideValue }: { e?: ChangeEvent<HTMLInputElement>; overrideValue?: typeof value }) => {
    let v = overrideValue ?? value;

    // return if nothing changed
    if (defaultValue === v) {
      return;
    }

    try {
      if (inputType === "number" && isEmpty(value) && e !== undefined) {
        v = null;
        // reset number if invalid (onChange doesn't fire when not a number)
        e.target.value = "";
      }
      const rowIdColumn = String(table.options.meta?.rowIdColumn ?? "id");
      const id = table.options.meta?.getRowId?.(row.original);
      // update the table's data
      table.options.meta?.updateCell(row.index, cell.column.id, v);
      // is required, don't call api
      if (isEmpty(v) && isRequired) {
        setShowRequired(true);
        return;
      }
      // Add or Update
      // Note: id could be 0 which is falsey
      if (id === undefined) {
        // check if all required fields have values
        if (
          !row
            .getAllCells()
            .some(
              (c) =>
                c.id !== cell.id &&
                c.column.columnDef.meta?.required &&
                getParserType(c.column.columnDef.meta?.type || "TEXT").isEmpty(c.getValue() as CellValue | null),
            )
        ) {
          // add new row
          table.options.meta?.onAdd?.({ ...row.original, [field]: v }, row);
        }
      } else {
        // update row
        table.options.meta?.onEdit?.({ [rowIdColumn]: id, [field]: v } as Partial<T>, row);
      }
    } catch (error) {
      // TODO: if api call errors, update cell / row styles?
      console.error(error);
    }
  };

  const isSelect = columnType === "SELECT" && selectOptions !== undefined && typeof value === "string";

  return (
    <>
      {columnType === "USD" && !isEmpty(value) && <span>$</span>}
      {isSelect ? (
        <Select
          value={value}
          selectSize="medium"
          options={selectOptions}
          onChange={(v) => {
            setValue(v);
            save({ overrideValue: v });
          }}
          disabled={disabled}
        />
      ) : (
        <Input
          type={inputType}
          autoFocus={autofocus}
          value={typeof value === "boolean" ? String(value) : value ?? undefined}
          transparent
          placeholder={isRequired ? cell.column.columnDef.header + " *" : ""}
          inputSize="medium"
          wrapperClassName={inputType !== "checkbox" ? styles.tableInput : undefined}
          onBlur={(e) => {
            save({ e });
          }}
          onChange={(e) => {
            if (inputType === "checkbox") {
              setValue(parseValue(e.target.checked));
              save({ e, overrideValue: e.target.checked });
            } else {
              setValue(parseValue(e.target.value));
            }
          }}
          disabled={disabled}
          checked={typeof value === "boolean" && value}
        />
      )}
      {showRequired && isEmpty(value) && (
        <IconWarning size={16} color="rubyLightX2" className={styles.required} title="Required" />
      )}
    </>
  );
}

interface ParsedType {
  inputType: HTMLInputTypeAttribute;
  isEmpty: (v: CellValue | null) => boolean;
  parseValue: (v: CellValue) => CellValue | null;
}

const getParserType = (type: ColumnTypes): ParsedType => {
  if (type === "USD") {
    return typeParsers.NUMBER;
  }
  if (type in typeParsers) {
    return typeParsers[type as keyof typeof typeParsers];
  }
  return typeParsers.TEXT;
};

const typeParsers = {
  TEXT: {
    inputType: "string",
    isEmpty: (v: CellValue | null) => !v,
    parseValue: (v: CellValue) => v,
  },
  NUMBER: {
    inputType: "number",
    isEmpty: (v: CellValue | null) => {
      if (typeof v === "string" && !v.length) {
        return true;
      }
      const n = Number(v);
      return isNaN(n);
    },
    parseValue: (v: CellValue) => {
      if (typeof v === "string" && !v.length) {
        return "";
      }
      const n = Number(v);

      return isNaN(n) ? "" : n;
    },
  },
  BOOLEAN: {
    inputType: "checkbox",
    isEmpty: () => false,
    parseValue: (v: CellValue) => v,
  },
  DATE: {
    inputType: "date",
    isEmpty: (v: CellValue | null) => !v,
    parseValue: (v: CellValue) => v,
  },
  DATETIME: {
    inputType: "datetime-local",
    isEmpty: (v: CellValue | null) => !v,
    parseValue: (v: CellValue) => v,
  },
  SELECT: {
    inputType: "string",
    isEmpty: (v: CellValue | null) => !v,
    parseValue: (v: CellValue) => v,
  },
} satisfies Partial<Record<ColumnTypes, ParsedType>>;
